<template>
  <div class="dv2-section">
    <SectionHeader icon="inventory_2" title="Análise de Mix/Itens" anchor="sec-itens" />

    <div class="dv2-grid dv2-grid--kpi-chart">
      <KpiCard :columns="2" :loading="loading">
        <KpiTile v-for="tile in itemsKpis" :key="tile.label" v-bind="tile" />
      </KpiCard>

      <HighchartsCard
        title="Itens por mês — cotados, com proposta, comprados e taxas de conversão"
        :tooltip="tooltips.conversion"
        :loading="loading"
        :options="conversionOptions"
      />
    </div>

    <div class="dv2-grid dv2-grid--full">
      <TableCard
        title="Curva ABC de produtos"
        :tooltip="tooltips.productsCurve"
        :loading="loading"
      >
        <a-table
          :columns="columns"
          :data-source="productsCurve"
          :pagination="false"
          :row-key="(row) => row.product"
          size="small"
        />
      </TableCard>
    </div>
  </div>
</template>

<script>
import SectionHeader from './SectionHeader.vue';
import KpiCard from './KpiCard.vue';
import KpiTile from './KpiTile.vue';
import HighchartsCard from './HighchartsCard.vue';
import TableCard from './TableCard.vue';
import DashboardV2Service from '@/services/v3/dashboard/DashboardV2Service';
import { baseTheme, xAxisDefaults, yAxisDefaults, pluck } from '../shared/highchartsTheme';
import { BRAND } from '../shared/constants';
import {
  brl, numberBR, pctBR, buildDeltaColumns, formatMonthLabel,
} from '../shared/format';
import { buildKpiTiles } from '../shared/kpi';

const service = new DashboardV2Service();

const TOOLTIPS = {
  conversion: 'Colunas: itens distintos cotados, com proposta e comprados por mês. Linhas: cobertura do cadastro e taxas de conversão.',
  productsCurve: 'Produtos ordenados pelo valor comprado, com participação sobre o total e variação contra mês e ano anteriores.',
};

const signed = (v, decimals = 1) => `${v >= 0 ? '+' : '-'}${pctBR(Math.abs(v), decimals)}`;

const ITEMS_STYLE = {
  inRegistry: {
    label: 'No cadastro',
    tooltip: 'Itens distintos ativos no cadastro do cliente. É a base para a cobertura de cotação.',
    format: 'int', valueColor: BRAND.preto, captionColor: BRAND.muted,
    caption: 'base de itens ativos',
    // tamanho do cadastro é uma métrica base, não julgada como boa/ruim
    deltaNeutral: true,
  },
  quoted: {
    label: 'Cotados',
    tooltip: 'Itens distintos que entraram em ao menos uma cotação no período. A barra mostra a cobertura do cadastro.',
    format: 'int', valueColor: BRAND.laranja, captionColor: BRAND.laranja, progressColor: BRAND.laranja,
    captionTemplate: (raw) => `${pctBR(raw.progress)} do cadastro · mês ${signed(raw.monthDeltaPct)}`,
  },
  proposed: {
    label: 'Com proposta',
    tooltip: 'Itens cotados que receberam ao menos uma proposta. A barra mostra a conversão cotado → com proposta.',
    format: 'int', valueColor: BRAND.azul, captionColor: BRAND.azul, progressColor: BRAND.azul,
    captionTemplate: (raw) => `${pctBR(raw.progress)} dos cotados · mês ${signed(raw.monthDeltaPct)}`,
  },
  purchased: {
    label: 'Comprados',
    tooltip: 'Itens que resultaram em pedido de compra. A barra mostra a conversão cotado → comprado.',
    format: 'int', valueColor: BRAND.verde, captionColor: BRAND.verde, progressColor: BRAND.verde,
    captionTemplate: (raw) => `${pctBR(raw.progress)} dos cotados · mês ${signed(raw.monthDeltaPct)}`,
  },
};

export default {
  name: 'ItemsSection',

  components: {
    SectionHeader, KpiCard, KpiTile, HighchartsCard, TableCard,
  },

  props: {
    filters: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      loading: true,
      tooltips: TOOLTIPS,
      itemsKpis: [],
      conversionChart: null,
      productsCurve: [],
    };
  },

  computed: {
    conversionOptions() {
      if (!this.conversionChart) return {};
      const rows = this.conversionChart;
      const quotedItems = pluck(rows, 'quotedItems');
      const proposedItems = pluck(rows, 'proposedItems');
      const purchasedItems = pluck(rows, 'purchasedItems');
      const registryCoverage = pluck(rows, 'registryCoverage');
      const quotedToProposedRate = pluck(rows, 'quotedToProposedRate');
      const quotedToPurchasedRate = pluck(rows, 'quotedToPurchasedRate');
      const theme = baseTheme();

      return {
        ...theme,
        xAxis: xAxisDefaults({ categories: pluck(rows, 'month').map(formatMonthLabel), crosshair: true }),
        yAxis: [
          yAxisDefaults({ title: { text: 'Itens' }, labels: { style: { fontSize: '11px', color: BRAND.cinza }, formatter() { return numberBR(this.value); } } }),
          yAxisDefaults({
            opposite: true, gridLineWidth: 0, min: 0, max: 100, title: { text: 'Percentual (%)' }, labels: { format: '{value}%' },
          }),
        ],
        tooltip: { ...theme.tooltip, shared: true },
        plotOptions: { ...theme.plotOptions, column: { borderRadius: 2, groupPadding: 0.06 } },
        series: [
          {
            type: 'column', name: 'Itens cotados', data: quotedItems, color: '#FFC1AD',
            tooltip: { pointFormatter() { return `<span style="color:${this.color}">●</span> Itens cotados: <b>${numberBR(this.y)}</b><br/>`; } },
          },
          {
            type: 'column', name: 'Itens com proposta', data: proposedItems, color: BRAND.azul,
            tooltip: { pointFormatter() { return `<span style="color:${this.color}">●</span> Itens com proposta: <b>${numberBR(this.y)}</b><br/>`; } },
          },
          {
            type: 'column', name: 'Itens comprados', data: purchasedItems, color: BRAND.verde,
            tooltip: { pointFormatter() { return `<span style="color:${this.color}">●</span> Itens comprados: <b>${numberBR(this.y)}</b><br/>`; } },
          },
          {
            type: 'spline', name: 'Cobertura do cadastro', data: registryCoverage, yAxis: 1, color: BRAND.laranja, lineWidth: 2, dashStyle: 'Dot',
            marker: { fillColor: '#fff', lineColor: BRAND.laranja, lineWidth: 2, radius: 3 },
            tooltip: { valueSuffix: '% do cadastro' },
          },
          {
            type: 'spline', name: 'Cotado → com proposta', data: quotedToProposedRate, yAxis: 1, color: BRAND.azul, lineWidth: 3, dashStyle: 'ShortDash',
            marker: { fillColor: '#fff', lineColor: BRAND.azul, lineWidth: 2, radius: 3 },
            tooltip: { valueSuffix: '%' },
          },
          {
            type: 'spline', name: 'Cotado → comprado', data: quotedToPurchasedRate, yAxis: 1, color: BRAND.preto, lineWidth: 3,
            marker: { fillColor: '#fff', lineColor: BRAND.preto, lineWidth: 2, radius: 4 },
            tooltip: { valueSuffix: '%' },
          },
        ],
      };
    },

    columns() {
      const h = this.$createElement;
      const totalApproved = this.productsCurve.reduce((sum, row) => sum + row.approved, 0) || 1;

      const base = [
        {
          title: 'Produto',
          dataIndex: 'product',
          customRender: (text) => h('span', { style: { fontWeight: 700 } }, text),
        },
        {
          title: 'Cotações',
          dataIndex: 'quotes',
          align: 'right',
          sorter: (a, b) => a.quotes - b.quotes,
        },
        {
          title: 'Com compra',
          dataIndex: 'quotes',
          align: 'right',
          customRender: (v, row) => {
            const conv = row.purchasedItems / row.quotedItems;
            return `${Math.round(v * conv)} · ${Math.round(conv * 100)}%`;
          },
        },
        {
          title: 'Valor comprado',
          dataIndex: 'approved',
          align: 'right',
          customRender: (v) => brl(v),
        },
        {
          title: 'Participação (%)',
          dataIndex: 'approved',
          align: 'right',
          width: 190,
          customRender: (v) => {
            const percent = (v / totalApproved) * 100;
            return h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' } }, [
              h('a-progress', {
                style: { width: '90px' },
                props: { percent, showInfo: false, strokeColor: BRAND.laranja, strokeWidth: 6 },
              }),
              h('span', { style: { fontSize: '11px', fontWeight: 600 } }, pctBR(percent)),
            ]);
          },
        },
      ];

      return base.concat(buildDeltaColumns(h));
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.fetch();
      },
    },
  },

  created() {
    this.fetch();
  },

  methods: {
    fetch() {
      this.loading = true;
      service.getItens(this.filters).then(({ data }) => {
        this.itemsKpis = buildKpiTiles(data.kpis, ITEMS_STYLE, { valueFontSize: '36px' });
        this.conversionChart = data.historic;
        this.productsCurve = data.ranking;
        this.loading = false;
      });
    },
  },
};
</script>
